import {UploadedFile} from "../models/interfaces/uploaded-file.interface";
import {LocalStorageKeys} from "@common/models/enums/local-storage-keys.enum";
import LocalStorageService from "@common/services/local-storage.service";
import {FileMetadataDto} from "../../core/models/interfaces/file-metadata-dto.interface";

type FileManagerMode = 'draft' | 'edit';

export class ArticleFileManager {
    private static mode: FileManagerMode = 'draft';

    static setMode(mode: FileManagerMode): void {
        this.mode = mode;
    }

    static getMode(): FileManagerMode {
        return this.mode;
    }

    private static getAllFilesKey(): LocalStorageKeys {
        return this.mode === 'draft'
            ? LocalStorageKeys.ARTICLE_DRAFT_ALL_FILES
            : LocalStorageKeys.ARTICLE_ALL_FILES;
    }

    private static getUsedFilesKey(): LocalStorageKeys {
        return this.mode === 'draft'
            ? LocalStorageKeys.ARTICLE_DRAFT_USED_FILES
            : LocalStorageKeys.ARTICLE_USED_FILES;
    }

    static getAllFiles(): UploadedFile[] {
        try {
            const uploadedFiles = LocalStorageService.get<UploadedFile[]>(this.getAllFilesKey()) as UploadedFile[]
            return uploadedFiles || [];
        } catch (error) {
            console.error('Błąd podczas pobierania wszystkich plików:', error);
            return [];
        }
    }

    static getUsedFiles(): string[] {
        try {
            const usedFiles = LocalStorageService.get<string[]>(this.getUsedFilesKey()) as string[]
            return usedFiles || [];
        } catch (error) {
            console.error('Błąd podczas pobierania używanych plików:', error);
            return [];
        }
    }

    static addFile(file: UploadedFile): void {
        try {
            const allFiles = this.getAllFiles();
            const usedFiles = this.getUsedFiles();
            const existingIndex = allFiles.findIndex(f => f.id === file.id);

            if (existingIndex >= 0) {
                allFiles[existingIndex] = file;
            } else {
                allFiles.push(file);
                usedFiles.push(file.id);
            }

            LocalStorageService.save(this.getAllFilesKey(), allFiles);
            LocalStorageService.save(this.getUsedFilesKey(), usedFiles);
        } catch (error) {
            console.error('Błąd podczas dodawania pliku:', error);
        }
    }

    static markFileAsUnused(fileId: string): void {
        try {
            const usedFiles = this.getUsedFiles();
            const filteredFiles = usedFiles.filter(id => id !== fileId);
            LocalStorageService.save(this.getUsedFilesKey(), filteredFiles);
        } catch (error) {
            console.error('Błąd podczas oznaczania pliku jako nieużywany:', error);
        }
    }

    static clearAllFiles(): void {
        try {
            LocalStorageService.remove(this.getAllFilesKey());
            LocalStorageService.remove(this.getUsedFilesKey());
        } catch (error) {
            console.error('Błąd podczas czyszczenia plików:', error);
        }
    }

    static initializeFilesFromArticle(files: FileMetadataDto[]): void {
        try {
            this.setMode('edit');
            LocalStorageService.save(this.getAllFilesKey(), files);
            LocalStorageService.save(this.getUsedFilesKey(), files.map(f => f.id));
        } catch (error) {
            console.error('Błąd podczas inicjalizacji plików z artykułu:', error);
        }
    }

    static syncUsedFilesFromContent(content: string): void {
        try {
            const allFiles = this.getAllFiles();
            let usedFileIds: string[] = [];

            const isJson = content.trim().startsWith('{') || content.trim().startsWith('[');

            if (isJson) {
                const imageNodes = this.findImageNodesFromJson(content);
                usedFileIds = imageNodes.map(node => node.id!)
            }

            LocalStorageService.save(this.getUsedFilesKey(), usedFileIds);

            allFiles.forEach(file => {
                file.isUsedInContent = usedFileIds.includes(file.id);
            });

            LocalStorageService.save(this.getAllFilesKey(), allFiles);
        } catch (error) {
            console.error('Błąd podczas synchronizacji używanych plików:', error);
        }
    }

    private static findImageNodesFromJson(jsonString: string) {
        let json;
        try {
            json = JSON.parse(jsonString);
        } catch (e) {
            console.error("❌ Nieprawidłowy JSON Lexicala:", e);
            return [];
        }

        const images: { id?: string; src: string; altText?: string }[] = [];

        function traverse(node: any) {
            if (!node || typeof node !== "object") return;

            if (node.type === "image") {
                const {id, src, altText} = node;
                if (src) {
                    images.push({id, src, altText});
                }
            }

            if (Array.isArray(node.children)) {
                node.children.forEach(traverse);
            }
        }

        if (json && json.root) {
            traverse(json.root);
        }

        return images;
    }
}
